import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Tag, Plus, Check, X } from 'lucide-react';
import { useRelationshipLabelsStore } from '../../lib/stores/relationshipLabelsStore';

interface Props {
  value: string[];                         // label_ids assegnati
  onChange: (labelIds: string[]) => void;
}

const SWATCHES = ['#6B7280', '#2383E2', '#16A34A', '#D97706', '#DC2626', '#7C3AED', '#DB2777', '#0891B2'];

/**
 * Multi-select delle association labels (stile HubSpot) su un link
 * persona↔azienda. Popover in portal (no clipping nel drawer) con creazione
 * inline di nuove etichette org-definite.
 */
export function RelationshipLabelPicker({ value, onChange }: Props) {
  const labels = useRelationshipLabelsStore((s) => s.labels);
  const fetchLabels = useRelationshipLabelsStore((s) => s.fetchLabels);
  const createLabel = useRelationshipLabelsStore((s) => s.createLabel);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => { if (labels.length === 0) fetchLabels(); }, [labels.length, fetchLabels]);

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const selected = labels.filter((l) => value.includes(l.id));
  const filtered = query.trim() ? labels.filter((l) => l.name.toLowerCase().includes(query.toLowerCase())) : labels;
  const exact = labels.find((l) => l.name.toLowerCase() === query.trim().toLowerCase());

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };
  const handleCreate = async () => {
    const color = SWATCHES[Math.abs(query.length) % SWATCHES.length];
    const created = await createLabel(query, color);
    if (created) { onChange([...value, created.id]); setQuery(''); }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {selected.map((l) => (
        <span key={l.id} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded" style={{ backgroundColor: l.color + '22', color: l.color }}>
          {l.name}
          <button onClick={() => toggle(l.id)} className="hover:opacity-70" title="Rimuovi"><X className="w-2.5 h-2.5" /></button>
        </span>
      ))}
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-blue-600 px-1 py-0.5 rounded"
        title="Etichette"
      >
        <Tag className="w-3 h-3" /> {selected.length === 0 ? 'Etichetta' : ''}
      </button>

      {open && pos && createPortal(
        <div
          ref={popRef}
          className="fixed z-[70] w-56 bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-gray-600 rounded-lg shadow-xl overflow-hidden"
          style={{ top: pos.top, left: pos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-1.5 border-b border-[rgba(55,53,47,0.08)] dark:border-gray-700">
            <input
              autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca o crea etichetta…"
              className="w-full px-2 py-1.5 text-sm bg-[rgba(55,53,47,0.03)] dark:bg-white/5 rounded outline-none text-[#37352F] dark:text-white"
            />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.map((l) => (
              <button key={l.id} onClick={() => toggle(l.id)} className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-[rgba(55,53,47,0.05)] dark:hover:bg-white/5 text-left">
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
                  <span className="truncate text-[#37352F] dark:text-white">{l.name}</span>
                </span>
                {value.includes(l.id) && <Check className="w-3.5 h-3.5 flex-shrink-0 text-[#2383E2]" />}
              </button>
            ))}
            {query.trim() && !exact && (
              <button onClick={handleCreate} className="w-full flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#2383E2] hover:bg-[#2383E2]/10 text-left">
                <Plus className="w-3.5 h-3.5" /> Crea “{query.trim()}”
              </button>
            )}
            {filtered.length === 0 && !query.trim() && (
              <div className="px-3 py-2 text-xs text-[#787774] dark:text-white/40">Nessuna etichetta. Scrivi per crearne una.</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
